import { createSelector } from 'reselect'
import { chain } from 'immutadot'
import { getMediaJumpStep } from '../configuration/selectors'
import { isCollaborativeIndexationCreationOpened } from '../collaborativeIndexations/selectors'
import { isPrintPreviewOpened } from '../print/selectors'

export const getNavigation = state => state.navigation

export const getMedia = createSelector(
    getNavigation,
    navigation => navigation && navigation.media
)

export const isNavigationEnabled = createSelector(
    isPrintPreviewOpened,
    isCollaborativeIndexationCreationOpened,
    (isPrintPreviewOpened, isCollaborativeIndexationCreationOpened) => ! isPrintPreviewOpened && ! isCollaborativeIndexationCreationOpened
)

export const getGroups = createSelector(
    getNavigation,
    navigation => navigation && navigation.groups
)

export const getMediaIndex = createSelector(
  getMedia,
  media => media && Number(media.index),
)

export const getMediaPosition = createSelector(
  getMediaIndex,
  mediaIndex => mediaIndex + 1,
)

export const getMediaCount = createSelector(
    getMedia,
    media => media && media.count,
)

export const initializeSelector = createSelector(
    context => context,
    initialize,
)

export const jumpToMediaSelector = createSelector(
    getNavigation,
    (state, index) => index,
    jumpTo,
)

export const forwardMediaSelector = createSelector(
    getNavigation,
    getMediaIndex,
    getMediaJumpStep,
    (navigation, mediaIndex, mediaJumpStep) => jumpTo(navigation, mediaIndex + mediaJumpStep)
)

export const backwardMediaSelector = createSelector(
    getNavigation,
    getMediaIndex,
    getMediaJumpStep,
    (navigation, mediaIndex, mediaJumpStep) => jumpTo(navigation, mediaIndex - mediaJumpStep),
)

export const previousMediaSelector = createSelector(
    getNavigation,
    navigation => jumpTo(navigation, navigation.media.index - 1),
)

export const nextMediaSelector = createSelector(
    getNavigation,
    navigation => jumpTo(navigation, navigation.media.index + 1),
)

export const firstMediaSelector = createSelector(
    getNavigation,
    navigation => jumpTo(navigation, 0)
)

export const lastMediaSelector = createSelector(
    getNavigation,
    getMediaCount,
    (navigation, count) => jumpTo(navigation, count - 1)
)

export const getGroupIndex = createSelector(
    getGroups,
    groups => groups && Number(groups.index),
)

export const getGroupPosition = createSelector(
    getGroupIndex,
    groupIndex => groupIndex + 1,
)

export const previousGroupPositionSelector = createSelector(
    getNavigation,
    navigation => jumpToGroup(navigation, navigation.groups.index - 1)
)

export const nextGroupPositionSelector = createSelector(
    getNavigation,
    navigation => jumpToGroup(navigation, navigation.groups.index + 1)
)

export const getGroupsCount = createSelector(
    getGroups,
    groups => groups && groups.count,
)

export const jumpToGroupPositionSelector = createSelector(
    getGroupsCount,
    (state, index) => index,
    (count, index) => Math.max(Math.min(index, count - 1), 0)
)

export const getPrevNextPropsAction = props => props.action

export const isPreviousAction = createSelector(
    getPrevNextPropsAction,
    (action) => action === 'prev'
)

export const isNextAction = createSelector(
    getPrevNextPropsAction,
    (action) => action === 'next'
)

export const hasPreviousMedia = createSelector(
    getMedia,
    media => media && media.hasPrevious,
)

export const hasNextMedia = createSelector(
    getMedia,
    media => media && media.hasNext,
)

export const hasPreviousGroup = createSelector(
    getGroups,
    groups => groups && groups.hasPrevious,
)

export const hasNextGroup = createSelector(
    getGroups,
    groups => groups && groups.hasNext,
)

export const isPrevMediaNavEnabled = createSelector(
    hasPreviousMedia,
    isNavigationEnabled,
    (hasPreviousMedia, isNavigationEnabled) => hasPreviousMedia && isNavigationEnabled
)

export const isNextMediaNavEnabled = createSelector(
    hasNextMedia,
    isNavigationEnabled,
    (hasNextMedia, isNavigationEnabled) => hasNextMedia && isNavigationEnabled
)

export const isPrevGroupNavEnabled = createSelector(
    hasPreviousGroup,
    isNavigationEnabled,
    (hasPreviousGroup, isNavigationEnabled) => hasPreviousGroup && isNavigationEnabled
)

export const isNextGroupNavEnabled = createSelector(
    hasNextGroup,
    isNavigationEnabled,
    (hasNextGroup, isNavigationEnabled) => hasNextGroup && isNavigationEnabled
)

export const keepMediaIndexInBoundaries = createSelector(
    getMediaCount,
    (state, index) => index,
    (mediaCount, index) => Math.max(Math.min(index, mediaCount - 1), 0)
)

export const isFastNavigationEnabled = createSelector(
    getMediaCount,
    getMediaJumpStep,
    (count, mediaJumpStep) => count > mediaJumpStep,
)

function initialize(navigation)
{
    const { counts, positions: indexes } = navigation

    navigation = {
        groups: {
            count: counts.group,
            index: indexes.group,
        },
        media: {
            count: counts.media,
            index: indexes.media,
        }
    }

    return chain(navigation)
        .set('media.hasPrevious', isNewIndexValid(indexes.media - 1, counts.media))
        .set('media.hasNext', isNewIndexValid(indexes.media + 1, counts.media))
        .set('groups.hasPrevious', isNewIndexValid(indexes.group - 1, counts.group))
        .set('groups.hasNext', isNewIndexValid(indexes.group + 1, counts.group))
        .value()
}

function jumpTo(navigation, index)
{
    if(index < 0)
    {
        index = 0
    }

    if(index >= navigation.media.count)
    {
        index = navigation.media.count -1
    }

    return chain(navigation)
        .set('media.hasPrevious', isNewIndexValid(index - 1, navigation.media.count))
        .set('media.hasNext', isNewIndexValid(index + 1, navigation.media.count))
        .set('media.index', index)
        .value()
}

function jumpToGroup(navigation, newIndex)
{
    return isNewIndexValid(newIndex, navigation.groups.count) ? newIndex : navigation.groups.index
}

function isNewIndexValid(index, mediaCount)
{
   if(index < 0 || index >= mediaCount)
   {
       return false
   }

   return true
}
